export type ShapeRefEntry = { id: string; name: string };

export type ShapeRefResult =
  | { kind: "ok"; id: string }
  | { kind: "not_found" }
  | { kind: "ambiguous"; candidates: ShapeRefEntry[] };

/*
 * Resolve an agent-supplied shape reference (an id, or a shape NAME) against
 * one slide's shapes.
 *
 * Shape ids are unique; shape NAMES are not. Firm templates routinely reuse a
 * name across shapes that play different roles — KPMG Law's "Divider 1" layout
 * carries TWO shapes both called "Title 5" (the first title line, and the
 * second line beneath it). Resolving such a name to the FIRST match, as this
 * did before, silently writes to a shape the agent did not mean: on the KPMG
 * master a set_text of "Title 5" landed both words in the upper box while the
 * lower box kept its own copy, stacking the second word on top of itself on
 * every section divider.
 *
 * So a name matching two or more shapes is AMBIGUOUS, never a guess. The caller
 * reports the candidates back to the agent, which re-issues the op against an
 * id. Pure, unit-tested.
 *
 * (The result is discriminated on a STRING `kind` rather than a boolean: this
 * project compiles without strictNullChecks, where boolean-literal discriminants
 * widen to `boolean` and stop narrowing at the call site.)
 */
export function resolveShapeRef(shapes: ShapeRefEntry[], ref: unknown): ShapeRefResult {
  const asString = String(ref);

  // An id is unique and unambiguous — it always wins, even when some other
  // shape happens to be NAMED that id.
  const byId = shapes.find((sh) => sh.id === asString);
  if (byId) return { kind: "ok", id: byId.id };

  const byName = shapes.filter((sh) => sh.name === asString);
  if (byName.length === 0) return { kind: "not_found" };
  if (byName.length === 1) return { kind: "ok", id: byName[0].id };
  return { kind: "ambiguous", candidates: byName };
}

/*
 * The names that cannot address a shape in this list, because two or more
 * shapes wear them. read_slide and inspect_deck flag these so the agent
 * targets by id from the start instead of learning it from a rejected op.
 *
 * Shares this module with resolveShapeRef deliberately: the flag and the
 * rejection must describe the same rule, or the agent gets told a name is fine
 * and then has the op refused.
 */
export function ambiguousShapeNames(shapes: ShapeRefEntry[]): Set<string> {
  const counts = new Map<string, number>();
  for (const sh of shapes) counts.set(sh.name, (counts.get(sh.name) ?? 0) + 1);
  const out = new Set<string>();
  counts.forEach((n, name) => {
    if (n > 1) out.add(name);
  });
  return out;
}
